import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { getRestockNotifications, markRestockSeen } from '../BaseURL';


const RestockNotificationDialog = ({
  pollInterval = 30000,
  seenByName   = null,
  onSeen       = null,
   onNotificationsLoaded = null,
}) => {
  const [open,    setOpen]    = useState(false);
  const [notifs,  setNotifs]  = useState([]);
  const [marking, setMarking] = useState(false);

  // ── Poll backend ────────────────────────────────────────────────────────
 const loadNotifications = useCallback(async () => {
  try {
    const res = await axios.get(getRestockNotifications);
    if (res.data.success && res.data.notifications.length > 0) {
      setNotifs(res.data.notifications);
      setOpen(true);
      if (typeof onNotificationsLoaded === 'function') {
        onNotificationsLoaded(res.data.notifications.length); // ← add this
      }
    } else {
      if (typeof onNotificationsLoaded === 'function') {
        onNotificationsLoaded(0); // ← reset to 0 when none
      }
    }
  } catch (err) {
    console.error('RestockNotificationDialog: failed to load notifications', err);
  }
}, [onNotificationsLoaded]);
  

  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, pollInterval);
    return () => clearInterval(interval);
  }, [loadNotifications, pollInterval]);

  // ── Mark as seen ────────────────────────────────────────────────────────
  const handleSeen = async () => {
    setMarking(true);
    try {
      const stored = localStorage.getItem('userInfo');
      const resolvedName =
        seenByName ||
        (stored ? JSON.parse(stored).name : null) ||
        'Pharmacist';

      await axios.put(markRestockSeen, { seenBy: resolvedName });

      setOpen(false);
      setNotifs([]);
      if (typeof onSeen === 'function') onSeen();
    } catch (err) {
      console.error('RestockNotificationDialog: failed to mark as seen', err);
      // Close anyway so pharmacist is never trapped
      setOpen(false);
      setNotifs([]);
    } finally {
      setMarking(false);
    }
  };




  // ── Nothing to show ─────────────────────────────────────────────────────
  if (!open) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {}}          // intentionally blocked
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 25px 80px rgba(0,0,0,0.35)',
          overflow: 'hidden',
          border: '3px solid #dc2626',
        },
      }}
    >
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 52, height: 52, borderRadius: '50%',
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 28, flexShrink: 0,
            }}
          >
            🔔
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, lineHeight: 1.2 }}>
              Restock Alert — Pharmacy
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)' }}>
              {notifs.length} drug{notifs.length !== 1 ? 's' : ''} need{notifs.length === 1 ? 's' : ''} restocking
            </Typography>
          </Box>
        </Box>

        {/* Pulsing count badge */}
        <Box
          sx={{
            bgcolor: 'white', color: '#dc2626', borderRadius: '50%',
            width: 36, height: 36, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: '1rem',
            animation: 'rnd-pulse 1.5s infinite',
            '@keyframes rnd-pulse': {
              '0%':   { boxShadow: '0 0 0 0 rgba(255,255,255,0.7)' },
              '70%':  { boxShadow: '0 0 0 10px rgba(255,255,255,0)' },
              '100%': { boxShadow: '0 0 0 0 rgba(255,255,255,0)' },
            },
          }}
        >
          {notifs.length}
        </Box>
      </Box>

      {/* ── Info banner ─────────────────────────────────────────────────── */}
      <Box
        sx={{
          px: 3, py: 1.5,
          bgcolor: '#fff7ed', borderBottom: '1px solid #fed7aa',
          display: 'flex', alignItems: 'center', gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 20 }}>⚠️</Typography>
        <Typography variant="body2" sx={{ color: '#92400e', fontWeight: 600 }}>
          Doctors submitted requests for drugs with insufficient dispensary stock.
          Please restock before dispensing.
        </Typography>
      </Box>

      {/* ── Notification list ────────────────────────────────────────────── */}
      <DialogContent sx={{ p: 0 }}>
        <Box
          sx={{
            maxHeight: 380, overflowY: 'auto',
            p: 2, display: 'flex', flexDirection: 'column', gap: 1.5,
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { bgcolor: '#f5f5f5', borderRadius: 3 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#d1d5db', borderRadius: 3 },
          }}
        >
          {notifs.map((notif, idx) => (
            <Box
              key={notif.id || idx}
              sx={{
                p: 2, borderRadius: 2,
                border: '1px solid #fecaca',
                bgcolor: idx % 2 === 0 ? '#fef2f2' : 'white',
                borderLeft: '5px solid #dc2626',
              }}
            >
              {/* Drug name + badge */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1e293b' }}>
                  💊 {notif.drug_name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    bgcolor: '#fee2e2', color: '#dc2626',
                    px: 1, py: 0.3, borderRadius: 1,
                    fontWeight: 700, flexShrink: 0, ml: 1,
                  }}
                >
                  LOW / OUT OF STOCK
                </Typography>
              </Box>

              {/* Stock numbers */}
              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
                <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: '#fff7ed', border: '1px solid #fed7aa' }}>
                  <Typography variant="caption" color="text.secondary" display="block">Requested</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#c2410c' }}>
                    {notif.requested_qty} units
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: '#fef2f2', border: '1px solid #fecaca' }}>
                  <Typography variant="caption" color="text.secondary" display="block">In Dispensary</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                    {notif.available_stock} units
                  </Typography>
                </Box>
                <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1.5, bgcolor: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <Typography variant="caption" color="text.secondary" display="block">Short by</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#15803d' }}>
                    {Math.max(0, notif.requested_qty - notif.available_stock)} units
                  </Typography>
                </Box>
              </Box>

              {/* Patient + doctor */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {notif.patient_name && (
                  <Typography variant="caption" color="text.secondary">
                    👤 Patient: <strong>{notif.patient_name}</strong>
                    {notif.patient_id ? ` (${notif.patient_id})` : ''}
                  </Typography>
                )}
                {notif.doctor_name && (
                  <Typography variant="caption" color="text.secondary">
                    🩺 By: <strong>{notif.doctor_name}</strong>
                  </Typography>
                )}
              </Box>

              {/* Timestamp */}
              {notif.created_at && (
                <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
                  {new Date(notif.created_at).toLocaleString()}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>

      {/* ── Action ──────────────────────────────────────────────────────── */}
      <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, flexDirection: 'column', gap: 1 }}>
        <Button
          fullWidth
          variant="contained"
          size="large"
          disabled={marking}
          onClick={handleSeen}
          startIcon={marking ? <CircularProgress size={18} sx={{ color: 'white' }} /> : null}
          sx={{
            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
            borderRadius: 2, py: 1.5,
            fontWeight: 700, textTransform: 'none', fontSize: '1rem',
            boxShadow: '0 4px 15px rgba(22,163,74,0.4)',
            '&:hover': {
              background: 'linear-gradient(135deg, #15803d 0%, #166534 100%)',
              boxShadow: '0 6px 20px rgba(22,163,74,0.5)',
            },
          }}
        >
          {marking ? 'Marking as Seen...' : '✅ Seen — I Will Restock These Drugs'}
        </Button>
        <Typography variant="caption" color="text.disabled" textAlign="center">
          This notification will not reappear once marked as seen
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

export default RestockNotificationDialog;